import useHeldValue from 'hooks/useHeldValue'
import { nonNullable, take } from 'lib/array/arrayUtils'
import { useMemo } from 'react'
import type { MapLayerGroup } from 'tripPlanner/contexts/MapLayers/MapLayersContext'
import type { PublicFullTrip, PublicTripItem } from 'tripPlanner/types/publicTrip'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { mapLEOfferToMarker } from '../utils'
import useExperiences from 'hooks/Experiences/useExperiences'
import type { FullTrip } from 'tripPlanner/types/common'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { extractSelectedItemId } from 'tripPlanner/utils/itemSelection'

export default function useLEExperiencePins(
  trip: FullTrip | PublicFullTrip,
  layerGroups: Array<MapLayerGroup>,
  bounds: string | undefined,
  currentSelectionId: string | undefined,
) {
  const experiencesLayerEnabled = !!layerGroups.find(group => group.id === 'leProducts')?.layers.find(layer => layer.id === 'experiences')?.isSelected
  const shouldUseExperiences = experiencesLayerEnabled && !!bounds

  const selectedExperienceId = extractSelectedItemId(currentSelectionId, 'experience')

  const { ids: _experienceIds = [] } = useExperienceSearchList({
    bounds,
    enabled: shouldUseExperiences,
  })
  const experienceIds = [
    ...(shouldUseExperiences ? take(_experienceIds, 30) : []),
    ...(selectedExperienceId && !_experienceIds.includes(selectedExperienceId) ? [selectedExperienceId] : []),
  ]

  const [_experiences] = useExperiences(experienceIds)
  const experiences = useHeldValue(_experiences, experiencesLayerEnabled && _experiences.length === 0)

  const experiencesMarkers = useMemo(() => {
    const savedOfferIds = new Set(nonNullable(trip.items.map((item: TripItem | PublicTripItem) => 'code' in item ? item.code : undefined)))
    return experiences
      .filter(experience => !savedOfferIds.has(experience.id))
      .map(mapLEOfferToMarker)
  }, [experiences, trip.items])

  return experiencesMarkers
}
